import { Point, Rectangle } from 'pixi.js';
import { QuadtreeNode } from './QuadtreeNode';

export class Quadtree<T> {

  private root: QuadtreeNode<T>;
  private bounds: Rectangle;
  private minimumBounds: Point;
  private objectNodeMapping: Map<T, QuadtreeNode<T>>;

  constructor(minX: number, minY: number, maxX: number, maxY: number, minimumSize: number = 100) {
    this.bounds = new Rectangle(minX, minY, maxX - minX, maxY - minY);
    this.minimumBounds = new Point(minimumSize, minimumSize);
    this.root = new QuadtreeNode<T>(this.bounds, this.minimumBounds);
    this.objectNodeMapping = new Map<T, QuadtreeNode<T>>();
  }

  public insert(object: T, bounds: Rectangle): boolean {
    const targetQuadtreeNode = this.root.insert(object, bounds);

    if (targetQuadtreeNode) {
      this.objectNodeMapping.set(object, targetQuadtreeNode);
    }

    return targetQuadtreeNode !== null;
  }

  public nodeForObject(object: T): QuadtreeNode<T> | undefined {
    return this.objectNodeMapping.get(object);
  }

  public remove(object: T): boolean {
    const node = this.objectNodeMapping.get(object);

    if (!node) {
      return false;
    }

    node.removeObject(object);
    this.objectNodeMapping.delete(object);

    return true;
  }

  public update(object: T, bounds: Rectangle): void {
    const wasRemoved = this.remove(object);
    if (!wasRemoved) {
      throw new Error("Can't update object which has not been added to Quadtree.");
    }
    this.insert(object, bounds);
  }

  public objectsInRectangle(rectangle: Rectangle): T[] {
    return this.root.objectInBounds(rectangle);
  }

  public getRoot(): QuadtreeNode<T> {
    return this.root;
  }
}
